import { accessMultiplier } from "./cities.ts";
import type { BucketKey, PlanInputs } from "./types.ts";

export const BUCKETS: BucketKey[] = ["life", "school", "undergrad", "elder", "health", "access"];

/**
 * Annual spending by bucket, in TODAY's rupees, before inflation.
 * Index 0 is the retirement year.
 */
export function bucketsToday(p: PlanInputs): Record<BucketKey, Float64Array> {
  const h = p.horizonYears;
  const s = p.spending;
  const hh = p.household;
  const zeros = () => new Float64Array(h);

  const out: Record<BucketKey, Float64Array> = {
    life: zeros(),
    school: zeros(),
    undergrad: zeros(),
    elder: zeros(),
    health: zeros(),
    access: zeros(),
  };

  // Lifestyle: only the locally-priced share moves with the city index.
  const lifePerYear = s.lifestylePricedLocally
    ? s.lifestylePerYear
    : s.lifestylePerYear * (s.localShare * s.cityIndex + (1 - s.localShare));
  out.life.fill(lifePerYear);

  // Health cover for the nuclear family, flat in real terms.
  out.health.fill(s.healthCoverPerYear);

  // Reaching services the town does not have. Zero in a metro. This is the cost
  // a plain cost-of-living index cannot see: a hill station is cheaper to live
  // in and more expensive to be old in, and the two do not cancel.
  if (s.remoteAccessPerYear > 0) {
    out.access.fill(
      s.remoteAccessPerYear * accessMultiplier(hh.childAges.length, hh.parentCount),
    );
  }

  const schoolPerChild = s.schoolPerChildPerYear * s.cityIndex;
  for (const startAge of hh.childAges) {
    // Schooling runs until the year before undergrad begins.
    for (let t = 0; t < h; t++) {
      if (startAge + t < s.undergradStartAge) out.school[t] += schoolPerChild;
    }
    // Undergrad: a fixed block of years starting at undergradStartAge.
    const begin = s.undergradStartAge - startAge;
    for (let t = begin; t < begin + s.undergradYears; t++) {
      if (t >= 0 && t < h) out.undergrad[t] += s.undergradPerYear;
    }
  }

  // Elder care: a step ramp keyed to the parents' age, scaled by how many.
  if (hh.parentCount > 0 && s.elderRamp.length > 0) {
    const ramp = [...s.elderRamp].sort((a, b) => a.fromAge - b.fromAge);
    const scale = (hh.parentCount / 2) * s.cityIndex; // ramp is quoted for two
    for (let t = 0; t < h; t++) {
      const age = hh.parentAge + t;
      if (age > s.elderEndAge) break;
      let perYear = 0;
      for (const step of ramp) if (age >= step.fromAge) perYear = step.perYear;
      out.elder[t] = perYear * scale;
    }
  }

  return out;
}

/** Per-bucket escalation rates, in the same order as BUCKETS. */
export function escalators(p: PlanInputs): Record<BucketKey, number> {
  return {
    access: p.inflation.medical,
    life: p.inflation.life,
    school: p.inflation.school,
    undergrad: p.inflation.undergrad,
    elder: p.inflation.medical,
    health: p.inflation.medical,
  };
}

/**
 * Nominal spending by bucket, escalated from today's prices through the
 * retirement date and along the whole horizon.
 */
export function nominalByBucket(p: PlanInputs): Record<BucketKey, Float64Array> {
  const today = bucketsToday(p);
  const esc = escalators(p);
  const out = {} as Record<BucketKey, Float64Array>;
  for (const k of BUCKETS) {
    const arr = new Float64Array(p.horizonYears);
    for (let t = 0; t < p.horizonYears; t++) {
      arr[t] = today[k][t] * Math.pow(1 + esc[k], t + p.yearsToRetirement);
    }
    out[k] = arr;
  }
  return out;
}

/** Total nominal spending per year, before tax gross-up. */
export function nominalSchedule(p: PlanInputs): Float64Array {
  const byBucket = nominalByBucket(p);
  const total = new Float64Array(p.horizonYears);
  for (const k of BUCKETS) {
    for (let t = 0; t < p.horizonYears; t++) total[t] += byBucket[k][t];
  }
  return total;
}
